"use client";

import React from "react";

// icons
import { faCalendarDays } from "@performyard/fontawesome/pro-regular-svg-icons";

// atoms
import { Icon } from "../Icon";
import { InputBase } from "../InputBase";
import type { InputBaseProps } from "../InputBase";

// Constants
const CALENDAR_ICON_SIZES = { small: 12, medium: 16 } as const;

/**
 * Props for the InputDate component
 */
export type InputDateProps = Omit<
	InputBaseProps,
	"value" | "onChange" | "type" | "inputMode" | "rightActionElement"
> & {
	/**
	 * The current input value (formatted date string)
	 */
	value: string;
	/**
	 * Callback when input value changes
	 */
	onChange: (value: string) => void;
	/**
	 * Callback when input loses focus
	 */
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	/**
	 * Callback when key is pressed
	 */
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	/**
	 * The placeholder text
	 * @default "mm/dd/yyyy"
	 */
	placeholder?: string;
	/**
	 * Callback when calendar icon is clicked
	 */
	onIconClick?: () => void;
};

/**
 * InputDate - Reusable date input component
 *
 * A specialized input component for date values that includes:
 * - Calendar icon for opening date picker
 * - Optional clear button (via InputBase clearable prop)
 * - Proper keyboard navigation
 * - Consistent styling with other inputs
 *
 * This component handles the input display and user interactions,
 * while the parent component manages the actual date logic and calendar.
 */
export const InputDate = React.forwardRef<HTMLDivElement, InputDateProps>(
	(
		{
			size,
			value,
			onChange,
			onBlur,
			onKeyDown,
			placeholder = "mm/dd/yyyy",
			disabled,
			dangerouslySetClassName,
			clearable = false,
			errored,
			id,
			onIconClick,
			...inputProps
		},
		ref
	) => {
		const inputRef = React.useRef<HTMLInputElement>(null);

		const handleIconClick = () => {
			if (!disabled) {
				if (onIconClick) {
					// Use the provided callback to open the calendar
					onIconClick();
				} else {
					// Fallback: focus the input
					inputRef.current?.focus();
				}
			}
		};

		const handleIconKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				handleIconClick();
			}
		};

		return (
			<div ref={ref} className="relative w-full">
				<InputBase
					{...inputProps}
					ref={inputRef}
					size={size}
					id={id}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					placeholder={placeholder}
					disabled={disabled}
					clearable={clearable}
					errored={errored}
					type="text"
					inputMode="text"
					dangerouslySetClassName={dangerouslySetClassName}
					rightActionElement={
						<div
							onClick={handleIconClick}
							onKeyDown={handleIconKeyDown}
							className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
							role="button"
							aria-label="Open date picker"
							aria-disabled={disabled}
							tabIndex={-1}
						>
							<Icon
								icon={faCalendarDays}
								size={CALENDAR_ICON_SIZES[size ?? "medium"]}
								color="neutral-5"
							/>
						</div>
					}
				/>
			</div>
		);
	}
);

InputDate.displayName = "InputDate";
